/**
 * AP Invoices Demo — early-payment discount approval (shared logic).
 *
 * THROWAWAY DEMO CODE. Approving the early-pay recommendation moves the
 * scheduled payment up to the discount date, rewrites the scheduled-payment
 * explanation, and adds the discount as a negative line. The banner on the
 * invoice slide-in and the procurement invoice-compliance page (which schedules
 * the same early payment) both go through `applyEarlyPayApproval`, so the AP
 * screen reads the same whichever side approved it.
 */

import type { ApDemoFixtureState } from './fixture-store.types';
import { formatDateOnlyString } from '@/utils/dateUtils';

/** Stable id for the discount line item added on approval (avoids duplicates). */
const DISCOUNT_LINE_ID = 'd1500000-0000-4000-8000-000000000001';

/** Parse the discount rate from terms like "2/10 Net 30" → 0.02. */
export function parseDiscountRate(terms: string | null | undefined): number {
  if (!terms) return 0;
  const match = terms.match(/(\d+(?:\.\d+)?)\s*\/\s*\d+/);
  return match ? Number(match[1]) / 100 : 0;
}

/**
 * `scheduledPaymentDate` is a timestamp field (real schema: `DataTypes.DATE`),
 * while `preferredPayDate` is DATEONLY. When we move the scheduled payment up to
 * the preferred date we must store a real timestamp — a bare `YYYY-MM-DD` parses
 * as UTC midnight and renders a day early in the date picker west of UTC. Anchor
 * at local noon (matching the demo seed) so it shows the right calendar day.
 */
function toScheduledTimestamp(value: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!dateOnly) return value; // already a full timestamp
  const [, y, m, d] = dateOnly;
  return new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    12,
    0,
    0,
    0,
  ).toISOString();
}

export function formatCurrency(amount: number, currency: string): string {
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
}

/**
 * Approve the early-payment discount on an AP invoice in the store. Does
 * nothing when the invoice is not in the store or carries no discount terms.
 */
export function applyEarlyPayApproval(
  s: ApDemoFixtureState,
  invoiceId: string,
): void {
  if (s.earlyPayApprovedByInvoiceId[invoiceId]) return;
  const detail = s.invoiceDetails[invoiceId];
  const invoice = detail?.invoice;
  if (!invoice?.scheduledPaymentDate) return;

  const rate = parseDiscountRate(invoice.paymentTerms);
  if (rate <= 0) return;

  const currency = invoice.currency || 'USD';
  // Base the discount on the (stable) pre-discount subtotal + tax, NOT the
  // totalAmount — approval rewrites totalAmount, and we must not re-discount it.
  const baseTotal =
    Number(invoice.subtotal ?? 0) + Number(invoice.taxAmount ?? 0);
  const original = baseTotal > 0 ? baseTotal : Number(invoice.totalAmount);
  const discounted = original * (1 - rate);
  const saving = original - discounted;
  // The invoice is scheduled near its due date; the agent moves the payment up
  // to the preferred (early-discount) date.
  const scheduledLabel = formatDateOnlyString(invoice.scheduledPaymentDate);
  const earlyDate = invoice.preferredPayDate ?? invoice.scheduledPaymentDate;
  const earlyLabel = formatDateOnlyString(earlyDate);
  // Persisted into the timestamp `scheduledPaymentDate` field on approval.
  const earlyScheduledTimestamp = toScheduledTimestamp(earlyDate);

  const explanation = `Early payment APPROVED — moved up to ${earlyLabel} (was ${scheduledLabel}), paying ${formatCurrency(
    discounted,
    currency,
  )} instead of ${formatCurrency(
    original,
    currency,
  )}, capturing the ${Math.round(rate * 100)}% discount (${formatCurrency(
    saving,
    currency,
  )} saved).`;
  const discountStr = (-saving).toFixed(2);
  const newTotalStr = discounted.toFixed(2);

  s.earlyPayApprovedByInvoiceId[invoiceId] = true;
  invoice.scheduledPaymentDate = earlyScheduledTimestamp;
  invoice.scheduledPaymentExplanation = explanation;
  invoice.totalAmount = newTotalStr;
  // Add the discount as a negative line item (once).
  if (!detail.lineItems.some((li) => li.id === DISCOUNT_LINE_ID)) {
    const maxLine = detail.lineItems.reduce(
      (m, li) => Math.max(m, li.lineNumber ?? 0),
      0,
    );
    detail.lineItems.push({
      id: DISCOUNT_LINE_ID,
      lineNumber: maxLine + 1,
      description: `Early-payment discount (${invoice.paymentTerms ?? `${Math.round(rate * 100)}%`})`,
      quantity: 1,
      unitPrice: discountStr,
      totalPrice: discountStr,
    });
  }
  const listInv = s.invoices.find((i) => i.id === invoiceId);
  if (listInv) {
    listInv.scheduledPaymentDate = earlyScheduledTimestamp;
    listInv.scheduledPaymentExplanation = explanation;
    listInv.totalAmount = newTotalStr;
  }
}
